import { useSearch } from "~/core/composables/useSearch";
import LxcError from "~/core/models/LxcError.class";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { FiltersSelection } from "~/core/models/filters";
import {
  Filters,
  FiltersType,
  OPERATOR_RSQL_EQUAL,
} from "~/core/models/filters";
import filtersUtils from "~/core/utils/filters.utils";
import type { GlobalDeviceConfiguration } from "~/modules/globalDeviceConfiguration/models/GlobalDeviceConfiguration.interface";
import globalDeviceConfigurationService from "~/modules/globalDeviceConfiguration/services/globalDeviceConfiguration.service";

export function useFetchGlobalDeviceConfigurations({
  organizationCode,
  searchMode,
}: UseFetchParameters) {
  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.GLOBAL_CONFIGURATION_ELIGIBILITY,
      {
        key: ["globalConfigurationEligibility"],
        operator: OPERATOR_RSQL_EQUAL,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const results: Ref<GlobalDeviceConfiguration[] | undefined> = ref();
  const isLoading: Ref<boolean> = ref(false);
  const error: Ref<LxcError | undefined> = ref();

  async function fetchAll() {
    isLoading.value = true;

    error.value = undefined;

    // force filtering on eligible only
    setFilter(Filters.GLOBAL_CONFIGURATION_ELIGIBILITY, "true");

    const response =
      await globalDeviceConfigurationService.getOrganizationAllDeviceModelSpecificConfigs(
        {
          organizationCode,
          searchParams: searchParams.value,
        },
      );

    if (LxcError.check(response)) {
      error.value = response;
    } else {
      results.value = response.map((deviceModelSpecificConfig) => {
        return {
          uid: deviceModelSpecificConfig.deviceModelUid,
          friendlyName: deviceModelSpecificConfig.deviceModelName,
          automaticRenewalDelayInitial:
            deviceModelSpecificConfig.external
              ?.automaticCertificateRenewalDelay,
          pollingFrequencyInitial: deviceModelSpecificConfig.external
            ?.pollingFrequency
            ? Math.round(
                deviceModelSpecificConfig.external?.pollingFrequency / 60,
              )
            : deviceModelSpecificConfig.external?.pollingFrequency,
        };
      });
    }

    isLoading.value = false;
  }

  return {
    isLoading,
    error,
    results,
    filters,
    fetchAll,
    setFilter,
    search,
    onSearch,
  };
}
